/**
 * Account-deletion grace banner (founder request 2026-07-13).
 *
 * When an owner signs back in DURING the 30-day deletion grace window,
 * the dashboard must make it obvious that the account is scheduled for
 * deletion and offer a one-click cancel — rather than dropping them on a
 * normal (empty-looking) dashboard where the only way to recover is to
 * hunt through Settings → danger zone. Renders app-wide, above the
 * content, on every authenticated view.
 */

import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { cancelDeletion, getDeletionStatus } from '../../api/account-deletion.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

function daysLeft(grace_until: string | null): number | null {
  if (grace_until === null) return null;
  const ms = new Date(grace_until).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function DeletionGraceBanner(): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useAuth();
  const toast = useToast();
  const [graceUntil, setGraceUntil] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [busy, setBusy] = useState(false);

  const authed = auth.state.kind === 'authenticated';

  const load = useCallback(async (): Promise<void> => {
    if (http === null || !authed) return;
    const res = await getDeletionStatus(http);
    if (res.kind === 'ok' && res.value.pending) {
      setPending(true);
      setGraceUntil(res.value.grace_until);
    } else {
      setPending(false);
    }
  }, [http, authed]);

  useEffect(() => {
    void load();
  }, [load]);

  const onCancel = async (): Promise<void> => {
    if (http === null) return;
    setBusy(true);
    const res = await cancelDeletion(http);
    if (res.kind === 'ok') {
      setPending(false);
      toast({ tone: 'lime', title: 'Deletion canceled. Bringing your sites back…' });
      // Hard-reload so the restored sites (and any other view that cached
      // the soft-deleted state) reappear without a manual refresh — founder
      // request 2026-07-13. Brief delay so the toast is seen first.
      if (typeof window !== 'undefined') {
        window.setTimeout(() => window.location.reload(), 900);
      }
      return;
    }
    setBusy(false);
    toast({ tone: 'coral', title: 'Could not cancel deletion.', body: res.message });
  };

  if (!authed || !pending) return null;

  const left = daysLeft(graceUntil);

  return (
    <div
      style={{
        background: C.surface,
        borderBottom: `2px solid ${C.coralFg}`,
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.coralFg, textTransform: 'uppercase' }}>
        Deletion scheduled
      </span>
      <span style={{ fontFamily: sansFont, fontSize: 13.5, color: C.text }}>
        This account is scheduled for permanent deletion
        {left !== null ? <> in <strong>{left} day{left === 1 ? '' : 's'}</strong></> : null}.
        Cancel now to keep it and bring your sites back online.
      </span>
      <button
        type="button"
        onClick={() => void onCancel()}
        disabled={busy}
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          fontWeight: 600,
          padding: '7px 16px',
          background: C.lime,
          color: C.ink,
          border: 'none',
          borderRadius: 3,
          cursor: 'pointer',
          opacity: busy ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        Cancel deletion &amp; restore
      </button>
    </div>
  );
}
